import React, { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { List, Map, fromJS } from 'immutable';

import searchReducer from 'data/search/reducer';
import { setFavoriteItem, updateFilter, updateLocalFilter } from 'data/search/actions';
import { LeadListScreen, leftPanelKeys } from 'app/Search/Header/searchFilterNew/shared/ConstantsNew';

/*
 * Local, modal-scoped replica of the `data/search` redux slice.
 *
 * The copied searchFilterNew panel components dispatch the exact same actions
 * (updateFilter / updateLocalFilter / removeFilterById / ...) they use on the
 * Search page, but here they run against a private useReducer store built from
 * the REAL data/search reducer. The global redux store (and therefore the
 * Search page) is never touched by this modal.
 */

const RESET = 'CUSTOM_FILTER_MODAL/RESET';

const FilterStateContext = createContext(null);

export const useFilterState = () => useContext(FilterStateContext);

const localReducer = (state, action) => (action.type === RESET ? action.state : searchReducer(state, action));

const toPlain = value => (value && typeof value.toJS === 'function' ? value.toJS() : value || {});

/*
 * Builds the initial slice from the incoming group filter/search object:
 * - `updateFilter` whitelists the legacy search fields through the reducer's
 *   own FilterFields constraint (same behavior as LOAD_SEARCH, minus location).
 * - The lead-list chip / tile highlight is seeded explicitly because LeadList
 *   derives its selection from `favoriteSelectedName`, not from the filter.
 * - Other sections re-hydrate their own applied-filter chips from the seeded
 *   filter values through their `useEffect([filter])` hooks.
 */
export const seedState = (search, availableListingTypes, leadListOptions) => {
  const plainSearch = toPlain(search);

  let slice = searchReducer(undefined, { type: '@@CUSTOM_FILTER_MODAL/INIT' });

  const listingTypes = List.isList(availableListingTypes) ? availableListingTypes : fromJS(availableListingTypes || []);
  slice = slice.set('availableListingTypes', listingTypes);

  slice = localReducer(slice, updateFilter(plainSearch));

  // Seed the lead-list highlight from the same option set the LeadList tiles
  // render (the group's carousel filters when provided), so the pre-selected
  // tile's label/value match exactly.
  const options = (leadListOptions && leadListOptions.length) ? leadListOptions : (LeadListScreen.fields.options || []);
  const type = plainSearch.listingType;
  const listingType = Array.isArray(type) ? type[0] : type;
  const option = listingType ? options.find(o => o.value === listingType) : null;

  if (option) {
    slice = localReducer(slice, updateLocalFilter([{
      key: leftPanelKeys.LeadLists,
      id: 'Lead Lists',
      name: option.label,
      option,
      apiKey: LeadListScreen.fields.name,
      removeID: LeadListScreen.fields.name,
      showALLDisplayValue: option.label,
    }]));
    slice = localReducer(slice, setFavoriteItem(option));
  }

  return slice;
};

export const FilterStateProvider = ({ seedSearch, availableListingTypes, leadListOptions, children }) => {
  const [initialSlice] = useState(() => seedState(seedSearch, availableListingTypes, leadListOptions));
  const [slice, dispatch] = useReducer(localReducer, initialSlice);

  const reset = useCallback(() => dispatch({ type: RESET, state: initialSlice }), [initialSlice]);

  // The data/search selectors expect the root immutable state with a 'search' key.
  const rootState = useMemo(() => Map({ search: slice }), [slice]);

  const value = useMemo(
    () => ({ rootState, slice, initialSlice, dispatch, reset, leadListOptions }),
    [rootState, slice, initialSlice, reset, leadListOptions],
  );

  return <FilterStateContext.Provider value={value}>{children}</FilterStateContext.Provider>;
};
